import { ForwardedRef, forwardRef } from 'react';
import Alert from './icons/Alert';
import Checkmark from './icons/Checkmark';
import { Input } from '@/lib/components/ui/input';
import { Label } from '@/lib/components/ui/label';
import { CheckIcon } from 'lucide-react';

export type TextInputAndLabelProps =
  React.InputHTMLAttributes<HTMLInputElement> & {
    id: string;
    label: string;
    description?: string;
    error?: string | null;
    className?: string;
    inputClassName?: string;
    valid?: boolean;
  };

const TextInputAndLabel = forwardRef(
  (
    {
      id,
      label,
      description,
      error,
      className = '',
      inputClassName = '',
      disabled,
      pattern,
      required,
      valid,
      ...rest
    }: TextInputAndLabelProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => (
    <div className={`w-full ${className}`}>
      <Label
        htmlFor={id}
        className={`flex gap-2 items-end text-base font-bold mb-2`}
      >
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          disabled={disabled}
          ref={ref}
          pattern={pattern}
          required={required}
          {...rest}
          className={`p-1.5 bg-white border ${
            error ? 'border-destructive!' : 'border-neutral-400'
          } text-base rounded-md block w-full peer ${inputClassName}`}
        />
        <CheckIcon
          color="white"
          className={`absolute right-2 top-2 p-0.5 size-5 aspect-square rounded-full bg-samsungBlue hidden ${
            pattern || required ? 'peer-valid:block' : valid ? 'block!' : ''
          }`}
        />
      </div>
      {description && (
        <p className="text-sm font-normal text-gray-500">{description}</p>
      )}
      {error && (
        <p className="flex items-center text-sm text-destructive pt-1">
          <Alert className="w-5 h-5 sm:w-3 sm:h-3 mr-1" /> {error}
        </p>
      )}
    </div>
  ),
);

export default TextInputAndLabel;
